<template>
  <div class="trade-chart">
    <template v-if="!assets">
      <div class="container__no-data">
        <md-icon class="md-size-4x">send</md-icon>
        <div class="text_large">{{ i18n.trd_no_pairs() }}</div>
        <div class="text_description text_margin text_medium">{{ i18n.trd_no_pairs_exp() }}</div>
      </div>
    </template>

    <template v-else>
      <div class="trade-chart__header">
        <div class="text_large">{{ i18n.trd_market_price() }}</div>
        <div class="trade-chart__header_balance text_medium">{{ i18n.trd_balance({base: baseAmount, quote: quoteAmount}) }}</div>
      </div>

      <div class="trade-chart__container">
        <div class="trade-chart__labels">

          <scale-tabs class="" :asset="assets" v-model="scale" :isPending="isPending"/>
          <div class="md-layout-item md-size-25 md-medium-size-30 md-small-size-40">
            <assets-select
              :pairs="pairs"
              :assets="assets"
            />
          </div>
        </div>
        <d3-chart v-if="history.length"
                  class="trade-chart__chart"
                  :data="roundChartVals(history)"
                  :volume="roundChartVals(normalizedVolume)"
                  :currency="assets.split('/')[1]"
                  :scale="scale"
                  :isPending="isPending"
                  :precision="precision"
                  :requiredTicks="requiredTicks"
        />
        <div  v-else class="chart-container__loader">Loading...</div>
      </div>

    </template>
  </div>

</template>

<script>
  import D3Chart from './ChartWidget.ChartRenderer'
  import ScaleTabs from './ChartWidget.DateTabs'
  import AssetsSelect from './ChartWidget.AssetsSelect'
  import { i18n } from '@js/i18n'
  import { mapGetters, mapActions } from 'vuex'
  import { vuexTypes } from '@vuex/types'
  import { DEFAULT_CONVERSION_ASSET } from '@const'
  import { attachEventHandler } from '@js/events/helpers'
  import { commonEvents } from '@js/events/common_events'
  import { CurrencyHelper } from '@js/helpers/currency.helper'
  import CurrencyRound from '@com/mixins/currency.round.mixin'

  export default {
    name: 'chart',
    mixins: [CurrencyRound],
    components: {
      ScaleTabs,
      D3Chart,
      AssetsSelect
    },

    props: {
      data: { type: Object, required: true },
      volume: { type: Object, required: true, default: {} },
      assets: { type: String, required: false },
      action: { type: String, default: 'issued' },
      currentCap: { type: [String, Number], required: false },
      hardCap: { type: [String, Number], required: false },
      softCap: { typ: [String, Number], required: false },
      precision: { type: Number, default: 0 },
      pairs: { type: Array, require: true, default: [] }
    },

    data () {
      return {
        scale: 'month',
        isPending: false,
        i18n,
        tokenCode: null,
        baseAmount: '',
        quoteAmount: '',
        DEFAULT_CONVERSION_ASSET,
        CurrencyHelper
      }
    },

    created () {
      this.tokenCode = this.tokenCodes[0] || null
      attachEventHandler(commonEvents.changePairsAsset, this.handleAssetChange)
      this.getTokens()
    },

    computed: {
      ...mapGetters([
        vuexTypes.userWithdrawableTokens,
        vuexTypes.assetPairs,
        vuexTypes.tokens,
        vuexTypes.accountBalances
      ]),
      tokenCodes () {
        return this.userWithdrawableTokens.map(token => token.code)
      },
      history () {
        if (!this.data[this.scale]) return []
        return this.data[this.scale]
      },
      requiredTicks () {
        return [this.softCap, this.hardCap].filter(value => value)
      },
      normalizedVolume () {
        if (!this.volume[this.scale]) return []
        return this.volume[this.scale]
      },
      baseAsset () {
        return this.assets.split('/')[0]
      },
      quoteAsset () {
        return this.assets.split('/')[1]
      },
      baseMarketCap () {
        const available = this.tokens.filter(item => item.code === this.baseAsset)[0].available
        const temparr = this.assetPairs.filter(item => item.base === this.baseAsset && item.quote === DEFAULT_CONVERSION_ASSET)
        console.log(temparr)
        if (temparr.length) {
          const price = temparr[0].currentPrice
          return price * available
        } else {
          return false
        }
      },
      quoteMarketCap () {
        const available = this.tokens.filter(item => item.code === this.quoteAsset)[0].available
        const temparr = this.assetPairs.filter(item => item.base === this.quoteAsset && item.quote === DEFAULT_CONVERSION_ASSET)
        console.log(temparr)
        if (temparr.length) {
          const price = temparr[0].currentPrice
          return price * available
        }
      }
    },
    methods: {
      ...mapActions({
        getTokens: vuexTypes.GET_ALL_TOKENS
      }),
      roundChartVals (data) {
        for (var i = 0; i < data.length; i++) {
          data[i].value = this.roundCurrency(data[i].value, this.assets)
        }
        return data
      },
      handleAssetChange (payload) {
        const base = payload.split('/')[0]
        const quote = payload.split('/')[1]
        const baseBalance = this.accountBalances[base]
        this.baseAmount = baseBalance ? `${i18n.c(baseBalance.balance)} ${this.CurrencyHelper.getCurrencyIcon(base)}` : `${i18n.c(0)} ${base}`
        this.quoteAmount = `${i18n.c(this.accountBalances[quote].balance)} ${this.CurrencyHelper.getCurrencyIcon(quote)}`
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "~scss/mixins";
  @import "~scss/variables";

  .trade-chart {

    &__header {
      display: flex;
      flex-wrap: nowrap;
      justify-content: space-between;

      &_balance {
        color: $col-text-secondary;
      }
    }

    &__labels {
      display: flex;
      justify-content: space-between;
      flex-wrap: nowrap;
      margin: 16px 0;
    }

    &__chart {
      padding: 12px;

      & /deep/ svg text {
        color: $col-text-secondary;
        fill: $col-text-secondary;
        font-size: 16px;
      }
    }
  }

  .chart-container__loader {
    text-align: center;
  }

</style>



// WEBPACK FOOTER //
// src/vue/app/trade/index/components/chart/Trade.ChartWidget.vue